import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';

import Sizes from '../definitions/Sizes';
import Colours from '../definitions/Colours';
import MessagePropType from '../definitions/MessagePropType';
import ACCORDION_VARIANTS from '../definitions/AccordionVariants';
import ACCORDION_MARKER_VARIANTS from '../definitions/AccordionMarkerVariants';
import ACCORDION_HEADER_SIZES from '../definitions/AccordionHeaderSizes';
import ACCORDION_HEADER_WEIGHTS from '../definitions/AccordionHeaderWeights';
import Dot from './Dot';
import IconChooser from './IconChooser';


// For minimal & default variants only
const FONT_CLASSES = {
    [ACCORDION_HEADER_SIZES.LARGE]: 'rh-text-l',
    [ACCORDION_HEADER_SIZES.MEDIUM]: 'rh-text-m',
    [ACCORDION_HEADER_SIZES.SMALL]: 'rh-text-s',
};

const DEFAULT_VARIANT_MARGIN_CLASSES = {
    [ACCORDION_HEADER_SIZES.LARGE]: 'rh-mr-1 rh-my-1_5',
    [ACCORDION_HEADER_SIZES.MEDIUM]: 'rh-mr-0_875 rh-my-1_125',
    [ACCORDION_HEADER_SIZES.SMALL]: 'rh-mr-0_75 rh-my-1',
};

// This component supports a sub-set of ACCORDION_VARIANTS.
const VARIANTS = {
    DEFAULT: ACCORDION_VARIANTS.DEFAULT,
    MINIMAL: ACCORDION_VARIANTS.MINIMAL,
};

function AccordionBasicItemHeader({
    isExpanded,
    onClick,

    title,
    titleColour = Colours.BLACKBERRY, // minimal only

    'data-name': dataName,

    marker = ACCORDION_MARKER_VARIANTS.NONE,
    iconKey,
    number,

    size = ACCORDION_HEADER_SIZES.LARGE,
    weight = ACCORDION_HEADER_WEIGHTS.REGULAR,

    disabled = false,
    variant = VARIANTS.DEFAULT,
    backgroundColour = Colours.TRANSPARENT,

    ...otherProps
}) {
    const isMinimal = variant === VARIANTS.MINIMAL;

    const computedSizeClass = FONT_CLASSES[size];

    const computedMarginClasses = DEFAULT_VARIANT_MARGIN_CLASSES[size];

    return (
        <AccordionToggleContainer
            className="rh-position-relative rh-display-flex rh-cursor-pointer"
            onClick={onClick}
            data-name={dataName}
            variant={variant}
            isExpanded={isExpanded}
            marker={marker}
            number={number}
            $backgroundColour={backgroundColour}

            {...otherProps}
        >
            <div className="headerContent rh-display-flex rh-py-0_25">
                <Choose>
                    <When condition={marker === ACCORDION_MARKER_VARIANTS.ICON}>
                        <div className="icon-container rh-mr-1 rh-display-flex rh-icon-s">
                            <IconChooser
                                iconKey={iconKey}
                                stroke={Colours.BLUEBERRY}
                            />
                        </div>
                    </When>
                    <When condition={marker === ACCORDION_MARKER_VARIANTS.DOT}>
                        <Dot
                            className="selected-dot rh-ml-0_75"
                        />
                    </When>
                </Choose>

                <h3 className={classNames(
                    'accordion-title',
                    {
                        'weight-regular': weight === ACCORDION_HEADER_WEIGHTS.REGULAR,
                        'weight-medium': weight === ACCORDION_HEADER_WEIGHTS.MEDIUM,

                        'rh-fg-stone': disabled,

                        'rh-ml-0_75': marker === ACCORDION_MARKER_VARIANTS.DOT,
                        'rh-fg-blueberry-dark': titleColour === Colours.BLUEBERRY_DARK && !disabled,
                    },
                    computedSizeClass,
                    computedMarginClasses,
                )}
                >
                    {title}
                </h3>

                {/* Empty div because we're using pseudo-elements */}
                <div className={classNames(
                    'expand-icon rh-mr-0_75 rh-position-relative',
                    isMinimal
                        ? 'rh-ml-0_75'
                        : 'rh-ml-auto',
                )}
                />
            </div>
        </AccordionToggleContainer>
    );
}

AccordionBasicItemHeader.propTypes = {
    isExpanded: PropTypes.bool,
    onClick: PropTypes.func,

    title: MessagePropType.isRequired,
    titleColour: PropTypes.oneOf([ Colours.BLACKBERRY, Colours.BLUEBERRY_DARK ]),

    'data-name': PropTypes.string,

    marker: PropTypes.oneOf(Object.values(ACCORDION_MARKER_VARIANTS)),
    iconKey: PropTypes.string,
    number: PropTypes.number,

    size: PropTypes.oneOf(Object.values(ACCORDION_HEADER_SIZES)),
    weight: PropTypes.oneOf(Object.values(ACCORDION_HEADER_WEIGHTS)),

    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(Object.values(VARIANTS)),
    backgroundColour: PropTypes.oneOf([ Colours.TRANSPARENT, Colours.COCONUT ]),
};

const TRANSITION_TIMING_LONG = '0.5s';
const TRANSITION_EASING_EASE = 'ease';
const TRANSITION_TIMING_SHORT = '0.3s';

const AccordionToggleContainer = styled.div`
    flex: 1;
    flex-direction: column;

    background-color: ${props => props.$backgroundColour};

    > .headerContent {
        flex: 1;
        align-items: center;

        // ICON
        ${props => props.marker === ACCORDION_MARKER_VARIANTS.ICON && `
            > .icon-container {
                flex-shrink: 0;
            }
        `}

        // DOT
        ${props => props.marker === ACCORDION_MARKER_VARIANTS.DOT && `
            > .selected-dot {
                flex-shrink: 0;

                transform: scale(0) translateX(-1em);
                transition: transform ${TRANSITION_TIMING_LONG} ${TRANSITION_EASING_EASE},
                width ${TRANSITION_TIMING_LONG} ${TRANSITION_EASING_EASE},
                height ${TRANSITION_TIMING_LONG} ${TRANSITION_EASING_EASE};

                ${props.isExpanded && `
                    transform: scale(0.5);
                `}
            }
        `}

        // NUMBER
        ${props => props.marker === ACCORDION_MARKER_VARIANTS.NUMBER
            && typeof props.number === 'number' && ` // allow 0 to be used!
            &::before {
                display: block;
                position: relative;
                content: "${props.number}";

                text-align: center;
                line-height: ${Sizes.SPACING.TWO};
                width: ${Sizes.SPACING.TWO};
                height: ${Sizes.SPACING.TWO};

                background: ${Colours.BLUEBERRY_LIGHT};
                color: ${Colours.BLUEBERRY};
                border-radius: 100%;
                font-weight: 500;
                margin-right: ${Sizes.SPACING.THREE_QUARTERS};
            }
        `}

        > .accordion-title {
            ${props => props.variant === VARIANTS.DEFAULT || props.variant === VARIANTS.MINIMAL && `
                transition:
                    transform ${TRANSITION_TIMING_LONG} ${TRANSITION_EASING_EASE},
                    color ${TRANSITION_TIMING_SHORT} ${TRANSITION_EASING_EASE};
            `}

            ${props => props.marker === ACCORDION_MARKER_VARIANTS.DOT && `
                transform: translateX( ${props.isExpanded
        ? Sizes.SPACING.QUARTER
        : '-1rem'});
            `}
        }

        > .expand-icon {
            /* need to set width and height to odd pixel value
                to center the +/- icon */
            min-width: 13px;
            height: 13px;

            &::before,
            &::after {
                position: absolute;

                display: block;
                content: '';

                width: 1px;
                background-color: ${Colours.BLACKBERRY};

                top: 0;
                bottom: 0;
                right: 50%;

                transition: background-color ${TRANSITION_TIMING_SHORT} ${TRANSITION_EASING_EASE},
                transform ${TRANSITION_TIMING_SHORT} ease-in-out;
            }

            &::before {
                ${props => props.isExpanded && `
                    transform: rotate(90deg);
                `};
            }

            &::after {
                transform: rotate(90deg);
            }
        }
    }

    &:hover {
        ${props => props.variant === VARIANTS.DEFAULT || props.variant === VARIANTS.MINIMAL && `
            .accordion-title {
                color: ${Colours.BLUEBERRY};
            }

            .expand-icon::before,
            .expand-icon::after {
                background-color: ${Colours.BLUEBERRY};
            }
        `};
    }
`;

export default AccordionBasicItemHeader;
